import React, {useCallback, useEffect, useState} from 'react';
import {defaultSubtitleStyle, Subtitle, SubtitleStyle} from '@/models/Subtitle';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faA, faBold, faFill, faItalic, faTimes} from '@fortawesome/free-solid-svg-icons';
import Popover from '@/Components/Popover';
import {isEqual} from "@/utils/objectUtils";
import CreatableSelect from 'react-select/creatable';
import {selectStyles} from '@/styles/selectTheme';
import {Tooltip} from 'flowbite-react';

import type {ColorResult} from 'react-color';
// Extract SketchPicker safely for both SSR (CommonJS) and Browser (ESM)
import * as ReactColor from 'react-color';
import FontPicker from "@/Components/FontPicker";
// @ts-ignore
const SketchPicker = ReactColor.SketchPicker || ReactColor.default?.SketchPicker;

interface SubtitleStylePanelProps {
    subtitle: Subtitle | null;
    availableFonts: string[];
    allHaveSameStyle: boolean;
    lastEditSource: 'manual' | 'programmatic' | null;
    onStyleChange: (style: SubtitleStyle, applyToAll: boolean) => void;
    onClose: () => void;
}

interface StylePanelState {
    fontSize: number;
    fontFamily: string;
    color: string; // rgba format
    backgroundColor: string; // rgba format
    isBold: boolean;
    isItalic: boolean;
    centerX: number;
    centerY: number;
    shadowEnabled: boolean;
    shadowColor: string; // rgba format
    shadowDistance: number;
    backgroundMode: 'unified' | 'per-line'
    paddingX: number;
    paddingY: number;
}

const SubtitleStylePanel: React.FC<SubtitleStylePanelProps> = ({
    subtitle,
    availableFonts,
    allHaveSameStyle,
    lastEditSource,
    onStyleChange,
    onClose,
}) => {
    const pickerWrapperClasses = "[&_.sketch-picker]:!border-0 [&_.sketch-picker]:!shadow-none [&_.sketch-picker]:!transparent dark:[&_.sketch-picker]:!bg-transparent [&_.sketch-picker_input]:!bg-white dark:[&_.sketch-picker_input]:!bg-gray-700 [&_.sketch-picker_input]:!text-gray-900 dark:[&_.sketch-picker_input]:!text-gray-100 [&_.sketch-picker_input]:!border-gray-200 dark:[&_.sketch-picker_input]:!border-gray-700 [&_.sketch-picker_label]:!text-gray-700 dark:[&_.sketch-picker_label]:!text-gray-300 [&_.sketch-picker>div:nth-last-child(2)]:!hidden";

    const [styleState, setStyleState] = useState<StylePanelState>({
        fontSize: defaultSubtitleStyle.fontSize!,
        fontFamily: defaultSubtitleStyle.fontFamily!,
        color: defaultSubtitleStyle.color!,
        backgroundColor: defaultSubtitleStyle.backgroundColor!,
        backgroundMode: defaultSubtitleStyle.backgroundMode!,
        isBold: defaultSubtitleStyle.fontWeight === 'bold',
        isItalic: defaultSubtitleStyle.fontStyle === 'italic',
        centerX: defaultSubtitleStyle.centerX!,
        centerY: defaultSubtitleStyle.centerY!,
        shadowEnabled: false,
        shadowColor: 'rgba(0, 0, 0, 0.9)',
        shadowDistance: 5,
        paddingX: defaultSubtitleStyle.paddingX!,
        paddingY: defaultSubtitleStyle.paddingY!,
    });

    useEffect(() => {
        if (subtitle) {
            const style = subtitle.style;

            const newState: StylePanelState = {
                fontSize: style.fontSize ?? defaultSubtitleStyle.fontSize!,
                fontFamily: style.fontFamily ?? defaultSubtitleStyle.fontFamily!,
                color: style.color ?? defaultSubtitleStyle.color!,
                backgroundColor: style.backgroundColor ?? defaultSubtitleStyle.backgroundColor!,
                backgroundMode: style.backgroundMode ?? defaultSubtitleStyle.backgroundMode!,
                isBold: (style.fontWeight ?? defaultSubtitleStyle.fontWeight) === 'bold',
                isItalic: (style.fontStyle ?? defaultSubtitleStyle.fontStyle) === 'italic',
                centerX: style.centerX ?? defaultSubtitleStyle.centerX!,
                centerY: style.centerY ?? defaultSubtitleStyle.centerY!,
                shadowEnabled: !!(style.shadowDistance || style.shadowColor),
                shadowColor: style.shadowColor ?? 'rgba(0, 0, 0, 0.9)',
                shadowDistance: style.shadowDistance ?? 5,
                paddingX: style.paddingX ?? defaultSubtitleStyle.paddingX!,
                paddingY: style.paddingY ?? defaultSubtitleStyle.paddingY!,
            };

            setStyleState(newState);
        }
    }, [subtitle]);

    const buildStyle = useCallback(() => {
        const {
            fontSize,
            fontFamily,
            color,
            backgroundColor,
            backgroundMode,
            isBold,
            isItalic,
            centerX,
            centerY,
            shadowEnabled,
            shadowColor,
            shadowDistance,
            paddingX,
            paddingY,
        } = styleState;

        const style: SubtitleStyle = {
            fontSize,
            fontFamily,
            color,
            backgroundColor,
            backgroundMode,
            fontWeight: isBold ? 'bold' : 'normal',
            fontStyle: isItalic ? 'italic' : 'normal',
            centerX,
            centerY,
            paddingX,
            paddingY
        };

        if (shadowEnabled) {
            style.shadowDistance = shadowDistance;
            style.shadowColor = shadowColor;
        }

        return style;
    }, [styleState]);

    const updateAndCommit = useCallback((updates: Partial<StylePanelState>) => {
        setStyleState(prev => {
            const newState = {...prev, ...updates};
            if (subtitle) {
                let newStyle: SubtitleStyle = {
                    fontSize: newState.fontSize,
                    fontFamily: newState.fontFamily,
                    color: newState.color,
                    backgroundColor: newState.backgroundColor,
                    backgroundMode: newState.backgroundMode,
                    fontWeight: newState.isBold ? 'bold' : 'normal',
                    fontStyle: newState.isItalic ? 'italic' : 'normal',
                    centerX: newState.centerX,
                    centerY: newState.centerY,
                    paddingX: newState.paddingX,
                    paddingY: newState.paddingY
                };

                if (newState.shadowEnabled) {
                    newStyle.shadowDistance = newState.shadowDistance;
                    newStyle.shadowColor = newState.shadowColor;
                }

                // Picking a background shape takes the background over from auto-mask
                if (subtitle.style.lineBounds && updates.backgroundMode === undefined) {
                    newStyle.lineBounds = subtitle.style.lineBounds;
                }

                if (isEqual(defaultSubtitleStyle, newStyle)) {
                    newStyle = {}
                }

                setTimeout(() => onStyleChange(newStyle, false), 0);
            }
            return newState;
        });
    }, [subtitle, onStyleChange]);

    const applyToAll = () => {
        onStyleChange(buildStyle(), true);
    };

    if (!subtitle) return null;

    return (
        <div className="bg-gray-50 dark:bg-[#181f2f]   lg:rounded-lg">
            <div className="px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1 xl:gap-2 flex-wrap">
                        {/* Font Size */}
                        <div className="md:w-20 [&_input]:!ring-0 [&_input]:!outline-none">
                            <CreatableSelect
                                value={{value: styleState.fontSize, label: String(styleState.fontSize)}}
                                onChange={(option) => {
                                    if (option && 'value' in option) {
                                        const size = parseInt(String(option.value));
                                        if (!isNaN(size) && size >= 8 && size <= 200) {
                                            updateAndCommit({fontSize: size});
                                        }
                                    }
                                }}
                                onCreateOption={(inputValue) => {
                                    const size = parseInt(inputValue);
                                    if (!isNaN(size) && size >= 8 && size <= 200) {
                                        updateAndCommit({fontSize: size});
                                    }
                                }}
                                options={[12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 44, 48, 54, 60, 72].map(size => ({
                                    value: size,
                                    label: String(size)
                                }))}
                                isSearchable
                                formatCreateLabel={(inputValue) => `${inputValue}px`}
                                className="text-sm"
                                components={{
                                    IndicatorSeparator: () => null
                                }}
                                styles={{
                                    ...selectStyles,
                                    control: (base, state) => ({
                                        ...selectStyles.control?.(base, state),
                                        minHeight: '32px',
                                        fontSize: '0.875rem',
                                    }),
                                    valueContainer: (base, state) => ({
                                        ...selectStyles.valueContainer?.(base, state),
                                        padding: '0 8px',
                                    }),
                                    dropdownIndicator: (base, state) => ({
                                        ...selectStyles.dropdownIndicator?.(base, state),
                                        padding: '4px',
                                    }),
                                }}
                            />
                        </div>

                        {
                            availableFonts.length > 0 && (
                                <>
                                    <div className="hidden md:block h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
                                    <div className="w-28  md:w-40 notranslate">
                                        <FontPicker
                                            value={styleState.fontFamily}
                                            onChange={(fontFamily) => updateAndCommit({fontFamily})}
                                            availableFonts={availableFonts}
                                        />
                                    </div>
                                </>
                            )
                        }

                        {/* Divider */}
                        <div className="hidden md:block h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

                        {/* Text Style Buttons */}
                        <button
                            onClick={() => updateAndCommit({isBold: !styleState.isBold})}
                            className={`w-7 h-7 hidden md:flex items-center justify-center rounded ${
                                styleState.isBold
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title="Bold"
                        >
                            <FontAwesomeIcon icon={faBold} className="w-3 h-3"/>
                        </button>
                        <button
                            onClick={() => updateAndCommit({isItalic: !styleState.isItalic})}
                            className={`w-7 h-7 hidden md:flex items-center justify-center rounded ${
                                styleState.isItalic
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title="Italic"
                        >
                            <FontAwesomeIcon icon={faItalic} className="w-3 h-3"/>
                        </button>

                        {/* Divider */}
                        <div className="h-6 w-px hidden md:flex bg-gray-300 dark:bg-gray-600"></div>

                        {/* Text Color */}
                        <Popover
                            trigger="click"
                            placement="bottom"
                            content={
                                <div className="pb-2">
                                    <div className="px-3 pt-3 ">
                                        <label className="text-xs font-bold text-gray-600 dark:text-gray-400 block">Text Color</label>
                                    </div>
                                    <div className={pickerWrapperClasses}>
                                        <SketchPicker
                                            color={styleState.color}
                                            onChange={(color: ColorResult) => updateAndCommit({
                                                color: `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, 1)`
                                            })}
                                            disableAlpha
                                            presetColors={[]}
                                        />
                                    </div>
                                </div>
                            }
                        >
                            <div
                                className="w-7 h-7 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex flex-col items-center justify-center"
                                title="Text Color"
                            >
                                <FontAwesomeIcon icon={faA} className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300"/>
                                <div className="w-5 h-1 rounded border mt-0.5"
                                     style={{backgroundColor: styleState.color}}></div>
                            </div>
                        </Popover>

                        {/* Background with Popover */}
                        <Popover
                            trigger="click"
                            placement="bottom"
                            content={
                                <div>
                                    <div className="px-3 pt-3 ">
                                        <label className="text-xs text-gray-600 font-bold dark:text-gray-400 block">Background Color</label>
                                    </div>
                                    <div className={pickerWrapperClasses}>
                                        <SketchPicker
                                            color={styleState.backgroundColor}
                                            onChange={(color: ColorResult) => updateAndCommit({
                                                backgroundColor: `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a ?? 1})`
                                            })}
                                            presetColors={[]}
                                        />
                                    </div>
                                    <div className="px-3 pb-3 pt-3  border-gray-200 dark:border-gray-700">
                                        <label className="text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 block">Background Style</label>
                                        <div className="flex gap-3">
                                            <div
                                                onClick={() => updateAndCommit({ backgroundMode: 'per-line' })}
                                                className="cursor-pointer flex-1 text-center"
                                            >
                                                <div className={`transition-all bg-gray-100 dark:bg-gray-800 ${
                                                    styleState.backgroundMode === 'per-line'
                                                        ? 'border-2 border-blue-600'
                                                        : 'border-2 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                } rounded w-full h-12 flex flex-col items-center justify-center gap-1 p-2`}>
                                                    <div className="bg-gray-300 dark:bg-gray-600 rounded-sm w-[80%] h-2"></div>
                                                    <div className="bg-gray-300 dark:bg-gray-600 rounded-sm w-[65%] h-2"></div>
                                                </div>
                                                <span className="text-xs mt-1 block text-gray-700 dark:text-gray-300">Per Line</span>
                                            </div>
                                            <div
                                                onClick={() => updateAndCommit({ backgroundMode: 'unified' })}
                                                className="cursor-pointer flex-1 text-center"
                                            >
                                                <div className={`transition-all ${
                                                    styleState.backgroundMode === 'unified'
                                                        ? 'border-2 border-blue-600'
                                                        : 'border-2 border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                                } rounded w-full h-12 flex flex-col items-center justify-center p-2`}>
                                                    <div className="bg-gray-300 dark:bg-gray-600 rounded-sm w-[90%] px-2 py-2 flex flex-col items-center justify-center gap-1">
                                                        <div className="w-full h-0.5 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                                                        <div className="w-[75%] h-0.5 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
                                                    </div>
                                                </div>
                                                <span className="text-xs mt-1 block text-gray-700 dark:text-gray-300">Unified</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="px-3 pb-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                                        <label className="text-xs font-bold text-gray-600 dark:text-gray-400 mb-2 block">Padding</label>
                                        <div className="flex gap-2">
                                            <div className="flex-1 relative" title="Horizontal padding">
                                                <svg width="16" height="16" viewBox="0 0 16 16" className="text-gray-500 dark:text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                                    <rect x="2.5" y="2.5" width="11" height="11" rx="1" stroke="currentColor" fill="none" strokeWidth="1" opacity="0.3" />
                                                    <path d="M3 3H6V13H3C2.44772 13 2 12.5523 2 12V4C2 3.44772 2.44772 3 3 3Z" fill="currentColor" opacity="0.3" />
                                                    <path d="M10 3H13C13.5523 3 14 3.44772 14 4V12C14 12.5523 13.5523 13 13 13H10V3Z" fill="currentColor" opacity="0.3"/>
                                                </svg>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="50"
                                                    value={styleState.paddingX}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value);
                                                        if (!isNaN(val) && val >= 1 && val <= 50) {
                                                            updateAndCommit({ paddingX: val });
                                                        }
                                                    }}
                                                    className="w-full pl-7 pr-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                            <div className="flex-1 relative" title="Vertical padding">
                                                <svg width="16" height="16" viewBox="0 0 16 16" className="text-gray-500 dark:text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                                    <rect x="2.5" y="2.5" width="11" height="11" rx="1" stroke="currentColor" fill="none" strokeWidth="1" opacity="0.3" />
                                                    <path d="M3 3H13C13.5523 3 14 3.44772 14 4V6H2V4C2 3.44772 2.44772 3 3 3Z" fill="currentColor" opacity="0.3" />
                                                    <path d="M2 10H14V12C14 12.5523 13.5523 13 13 13H3C2.44772 13 2 12.5523 2 12V10Z" fill="currentColor"  opacity="0.3"/>
                                                </svg>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="20"
                                                    value={styleState.paddingY}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value);
                                                        if (!isNaN(val) && val >= 1 && val <= 50) {
                                                            updateAndCommit({ paddingY: val });
                                                        }
                                                    }}
                                                    className="w-full pl-7 pr-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        >
                            <div
                                className="w-7 h-7 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 flex flex-col items-center justify-center"
                                title="Background"
                            >
                                <FontAwesomeIcon
                                    icon={faFill}
                                    className="w-3.5 h-3.5 text-gray-700 dark:text-gray-300"
                                />
                                <div className="w-5 h-1 mt-0.5 rounded border"
                                     style={{backgroundColor: styleState.backgroundColor}}></div>
                            </div>
                        </Popover>

                        {/* Shadow with Popover */}
                        <Popover
                            trigger="click"
                            placement="bottom"
                            content={
                                <div>
                                    <div className="px-3 py-2 flex items-center justify-between">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-20">Text
                                            Shadow</label>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={styleState.shadowEnabled}
                                                onChange={(e) => updateAndCommit({shadowEnabled: e.target.checked})}
                                                className="sr-only peer"
                                            />
                                            <div
                                                className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                    {styleState.shadowEnabled && (
                                        <>
                                            <div className="px-3 pt-3  border-t border-gray-200 dark:border-gray-600">
                                                <label className="text-xs text-gray-600 font-bold dark:text-gray-400 block">Shadow Color</label>
                                            </div>
                                            <div className={pickerWrapperClasses}>
                                                <SketchPicker
                                                    color={styleState.shadowColor}
                                                    onChange={(color: ColorResult) => updateAndCommit({
                                                        shadowColor: `rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a ?? 1})`
                                                    })}
                                                    presetColors={[]}
                                                />
                                            </div>
                                            <div className="px-3 pb-3 mt-3">
                                                <div>
                                                    <label
                                                        className="text-xs font-bold text-gray-600 dark:text-gray-400 mb-1 block">Distance</label>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="50"
                                                            value={styleState.shadowDistance}
                                                            onChange={(e) => updateAndCommit({shadowDistance: parseInt(e.target.value)})}
                                                            className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
                                                        />
                                                        <span
                                                            className="w-6 text-right text-sm text-gray-700 dark:text-gray-300">{styleState.shadowDistance}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            }
                        >
                            <div
                                className="w-7 h-7 rounded cursor-pointer hidden md:flex hover:bg-gray-100 dark:hover:bg-gray-700  items-center justify-center relative"
                                title="Shadow"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    className={`text-gray-700 dark:text-gray-300 ${!styleState.shadowEnabled ? 'opacity-30' : ''}`}
                                >
                                    <circle cx="6" cy="10" r="5.5" className="fill-white dark:fill-gray-700"
                                            stroke="currentColor"></circle>
                                    <path stroke="currentColor"
                                          d="m3.68 14.973 6.594-6.594M2.26 13.722l6.233-6.233m-7.45 4.779 9.231-9.231M.479 10.16l7.124-7.123m-1.776 12.46 5.661-5.661"></path>
                                    <circle cx="10" cy="6" r="5.5" className="fill-white dark:fill-gray-700"
                                            stroke="currentColor"></circle>
                                </svg>
                            </div>
                        </Popover>
                    </div>
                    <div className="flex items-center md:gap-2 flex-shrink-0">

                        {/* Apply to All */}
                        {!allHaveSameStyle && lastEditSource === 'manual' && (
                            <Tooltip
                                content={<span className="whitespace-nowrap text-sm">Apply current style to all subtitles</span>}
                                placement="bottom"
                                className="p-2 py-1 text-xs"
                            >
                                <button
                                    onClick={applyToAll}
                                    className="text-xs md:text-sm font-medium dark:text-blue-300 text-blue-700 hover:underline"
                                >
                                    Apply to All
                                </button>
                            </Tooltip>
                        )}

                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="w-7 h-7 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            title="Close"
                        >
                            <FontAwesomeIcon icon={faTimes} className="w-3 h-3"/>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubtitleStylePanel;
